import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { locationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { MapPin, Plus, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const LOCATION_TYPE_COLORS = {
  venue: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  warehouse: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  vendor: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  other: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
};

const Venues = () => {
  usePageTitle('Locations');
  const { isViewer } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [sortBy, setSortBy] = useState('location_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const navigate = useNavigate();

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    loadLocations();
  }, [search, page, limit, sortBy, sortOrder]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (search) params.search = search;
      
      const response = await locationsAPI.list(params);
      
      if (response.data.data && response.data.pagination) {
        setLocations(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setLocations(response.data);
        setPagination({
          total: response.data.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const renderPagination = () => {
    const { total, total_pages, has_next, has_prev } = pagination;
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, page - Math.floor(maxVisible / 2));
      let end = Math.min(total_pages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border/50">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{startItem}</span> to{' '}
          <span className="font-medium text-foreground">{endItem}</span> of{' '}
          <span className="font-medium text-foreground">{total}</span> locations
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows:</span>
            <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}>
              <SelectTrigger className="w-16 h-8 bg-background/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(1)} disabled={!has_prev}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page - 1)} disabled={!has_prev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              ))}
            </div>

            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page + 1)} disabled={!has_next}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(total_pages)} disabled={!has_next}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <MapPin className="h-8 w-8 text-amber-400" />
            Locations
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage venues, warehouses, vendor addresses, and other locations
          </p>
        </div>
        {!isViewer && (
          <Button asChild data-testid="add-location-btn">
            <Link to="/locations/new">
              <Plus className="h-4 w-4 mr-2" />
              Add Location
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search locations by name or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-background/50"
              data-testid="search-locations"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
            </div>
          ) : locations.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('location_name')}
                    >
                      <div className="flex items-center">
                        Name
                        {getSortIcon('location_name')}
                      </div>
                    </TableHead>
                    <TableHead className="data-table">Type</TableHead>
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('organization_name')}
                    >
                      <div className="flex items-center">
                        Organization
                        {getSortIcon('organization_name')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('city')}
                    >
                      <div className="flex items-center">
                        City/State
                        {getSortIcon('city')}
                      </div>
                    </TableHead>
                    <TableHead className="data-table">Load-In Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {locations.map((location) => (
                    <TableRow
                      key={location.id}
                      className="border-border/50 hover:bg-muted/30 cursor-pointer"
                      onClick={() => navigate(`/locations/${location.id}`)}
                      data-testid={`location-row-${location.id}`}
                    >
                      <TableCell className="font-medium">{location.location_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={LOCATION_TYPE_COLORS[location.location_type] || LOCATION_TYPE_COLORS.venue}>
                          {(location.location_type || 'venue').charAt(0).toUpperCase() + (location.location_type || 'venue').slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {location.organization_name || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {location.city && location.state ? `${location.city}, ${location.state}` : '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {location.load_in_type?.join(', ') || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {renderPagination()}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No locations found</p>
              <p className="text-sm mt-1">Add your first location to get started</p>
              <Button asChild className="mt-4" variant="outline">
                <Link to="/locations/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Location
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Venues;
